const HCW_QUESTION_MIN_AGE = 16;
const HCW_QUESTION_MAX_AGE = 69;

const INFLUENZA_TYPE = "Influenza";
// The catalogue carries both spellings; the mutation hook already has to allow for it.
const PNEUMOCOCCAL_TYPES = ["Pneumococcal", "Pneumococcol"];

const parseDateGiven = (dateGiven) => {
  if (!dateGiven) return null;

  // The form holds DD-MM-YYYY; a record loaded for editing can arrive as YYYY-MM-DD.
  const parts = String(dateGiven).split("-");

  if (parts.length !== 3) return null;

  const [first, second, third] = parts;
  const isIsoOrder = first.length === 4;
  const year = Number(isIsoOrder ? first : third);
  const month = Number(second);
  const day = Number(isIsoOrder ? third : first);
  const parsed = new Date(year, month - 1, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ageAt = (dateOfBirth, onDate) => {
  const birth = new Date(dateOfBirth);

  if (Number.isNaN(birth.getTime())) return null;

  let age = onDate.getFullYear() - birth.getFullYear();
  const monthDiff = onDate.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && onDate.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

/**
 * Whether "Is the client a Healthcare worker?" is put to the clinician (Phase 2 §5.3.1).
 *
 * Influenza only — Appendix 4 marks HCW "Do Not Ask" for COVID and Pneumococcal — and only
 * for a patient aged 16 to 69 inclusive **at the vaccination date**, not today's. A
 * retrospective record for a dose given the day before a 70th birthday still asks it.
 *
 * Mirrors `HealthlinkValidationService.isHealthcareWorkerQuestionAsked` so the form asks
 * exactly what the backend will require an answer to. A patient with no date of birth
 * cannot be placed in the band, so the question is not asked.
 */
export const isHealthcareWorkerQuestionAsked = ({
  vaccineType,
  dateOfBirth,
  dateGiven,
}) => {
  if (vaccineType !== INFLUENZA_TYPE || !dateOfBirth) return false;

  const administeredOn = parseDateGiven(dateGiven);

  if (!administeredOn) return false;

  const age = ageAt(dateOfBirth, administeredOn);

  return age !== null && age >= HCW_QUESTION_MIN_AGE && age <= HCW_QUESTION_MAX_AGE;
};

/**
 * Whether at least one Indication for Vaccination is required (Phase 2 §5.3.2).
 *
 * A Yes is the only answer that makes them optional. The counter-intuitive row is the
 * third: when the question is *not shown* because the patient is outside 16–69,
 * Indications become **more** strictly required, not less — "If the HCW question is not
 * available for the patient owing to the fact the HCW question is not shown based on age,
 * the Indications for vaccinations question should be considered mandatory."
 *
 * Pneumococcal falls out of the same rule without a special case: the question is never
 * asked for it, so it lands on that third row.
 */
export const isIndicationRequired = ({
  vaccineType,
  dateOfBirth,
  dateGiven,
  healthcareWorker,
}) => {
  const isIndicationProgramme =
    vaccineType === INFLUENZA_TYPE || PNEUMOCOCCAL_TYPES.includes(vaccineType);

  if (!isIndicationProgramme) return false;

  const isAsked = isHealthcareWorkerQuestionAsked({
    vaccineType,
    dateOfBirth,
    dateGiven,
  });

  return !(isAsked && healthcareWorker === true);
};
